import { CommonModule } from "@angular/common";
import { Component, OnInit, viewChild } from "@angular/core";
import { Router } from "@angular/router";
import { MatIcon } from "@angular/material/icon";
import { HoyEnElEquipoComponent } from "src/app/dashboard/hoy-en-el-equipo/hoy-en-el-equipo.component";
import { DashboardAsistenciaComponent } from "src/app/dashboard/pestanas/asistencia/dashboard-asistencia.component";
import { DashboardDefectosComponent } from "src/app/dashboard/pestanas/defectos/dashboard-defectos.component";
import { DashboardDenunciasInicioComponent } from "src/app/dashboard/pestanas/denuncias/dashboard-denuncias.component";
import { DashboardIaComponent } from "src/app/dashboard/pestanas/ia/dashboard-ia.component";
import { DashboardProyectosComponent } from "src/app/dashboard/pestanas/proyectos/dashboard-proyectos.component";
import { DashboardRiesgosComponent } from "src/app/dashboard/pestanas/riesgos/dashboard-riesgos.component";
import { DashboardTicketsComponent } from "src/app/dashboard/pestanas/tickets/dashboard-tickets.component";
import { DashboardTabDef, DashboardTabGestion, PestanaDashboard } from "src/app/dashboard/shared/pestana-dashboard";
import { tieneAccesoMenu } from "src/app/shared/menu/acceso-menu";
import { Menu } from "src/app/shared/model/security/menu.model";
import { AuthService } from "src/app/security/auth.service";

/**
 * Cascaron del Dashboard de Inicio: hero, "Hoy en el equipo" y una barra
 * de pestañas, una por modulo con indicadores. Cada pestaña es un
 * componente en pestanas/<modulo>/ que implementa PestanaDashboard y se
 * monta solo al elegirla (*ngIf), asi consulta sus datos hasta entonces.
 * La pestaña se ve unicamente si el usuario tiene la hoja de menu del
 * modulo (tieneAccesoMenu), igual que el acceso directo "Gestionar".
 */
@Component({
  selector: "app-dashini",
  standalone: true,
  templateUrl: "./dashini.component.html",
  styleUrls: ["./dashini.component.css", "../shared/dashboard-tarjetas.css"],
  imports: [
    CommonModule,
    DashboardAsistenciaComponent,
    DashboardDefectosComponent,
    DashboardDenunciasInicioComponent,
    DashboardIaComponent,
    DashboardProyectosComponent,
    DashboardRiesgosComponent,
    DashboardTicketsComponent,
    HoyEnElEquipoComponent,
    MatIcon,
  ],
})
export class DashiniComponent implements OnInit {
  /**
   * Pestañas registradas, en el orden en que se muestran.
   *
   * Para agregar una:
   *  1. Crear pestanas/<modulo>/dashboard-<modulo>.component.ts+.html
   *     (standalone, implements PestanaDashboard, styleUrls con
   *     ../../shared/dashboard-tarjetas.css).
   *  2. Añadir aqui su DashboardTabDef con el FACTORY de la HOJA de menu
   *     que la gatea (ver pestana-dashboard.ts).
   *  3. Importar el componente en `imports` y añadir en dashini.html un
   *     bloque `<app-dashboard-<modulo> #pestana *ngIf="activeDashboard === '<id>'">`.
   */
  readonly tabs: DashboardTabDef[] = [
    {
      id: "tickets",
      label: "Tickets",
      icon: "confirmation_number",
      menu: ["/TICKETS/Admin", "/TICKETS/Cte"],
      gestionar: { ruta: "/TICKETS/Admin", icon: "view_kanban", menu: "TICKETS/Admin" },
    },
    {
      id: "defectos",
      label: "Defectos",
      icon: "bug_report",
      menu: ["INC_QA", "INC_CLIENTE", "INC_DESARROLLO"],
      gestionar: { ruta: "/INCIDENCIAS/INC_QA", icon: "bug_report", menu: "INC_QA" },
    },
    {
      id: "asistencia",
      label: "Asistencia",
      icon: "schedule",
      menu: "asistencia/monitoreo",
      gestionar: { ruta: "/asistencia/monitoreo", icon: "groups", menu: "asistencia/monitoreo" },
    },
    {
      id: "proyectos",
      label: "Proyectos",
      icon: "account_tree",
      menu: ["VISUALIZARPROY", "admproyectos", "seguimiento", "consultaSeguimientos"],
      gestionar: { ruta: "/ODP/VISUALIZARPROY", icon: "account_tree", menu: "VISUALIZARPROY" },
    },
    {
      id: "denuncias",
      label: "Denuncias",
      icon: "report_problem",
      menu: ["DENUNCIAS/dashboard", "DENUNCIAS/kanban"],
      gestionar: { ruta: "/DENUNCIAS/dashboard", icon: "report_problem", menu: "DENUNCIAS/dashboard" },
    },
    {
      id: "riesgos",
      label: "Riesgos",
      icon: "policy",
      menu: ["gestionRiesgos", "matrizRiesgosIA"],
      gestionar: { ruta: "/RIESGOS/gestionRiesgos", icon: "policy", menu: "gestionRiesgos" },
    },
    {
      id: "ia",
      label: "IA",
      icon: "smart_toy",
      menu: [
        "admin/inventario-ia/gestion",
        "admin/inventario-ia/consulta",
        "admin/solicitudes-ia/gestion",
        "INCIDENCIAS/INC_IA",
        "admin/evaluaciones-ia",
      ],
      gestionar: { ruta: "/admin/inventario-ia/gestion", icon: "smart_toy", menu: "admin/inventario-ia/gestion" },
    },
  ];

  /** Las que el usuario puede ver, ya filtradas por su menu. */
  tabsVisibles: DashboardTabDef[] = [];
  activeDashboard = "";
  /** Accion "Gestionar" de la pestaña activa; null si no aplica o no tiene la hoja. */
  gestion: DashboardTabGestion | null = null;

  /**
   * La pestaña montada. Todas llevan la misma referencia #pestana y solo
   * una vive a la vez, asi que la consulta resuelve a la activa. Es una
   * consulta de señal (no @ViewChild) para que leerla en la plantilla no
   * dispare ExpressionChangedAfterItHasBeenChecked al cambiar de pestaña.
   */
  private readonly pestana = viewChild<PestanaDashboard>("pestana");

  private menus: Menu[] = [];

  constructor(
    private authService: AuthService,
    private router: Router,
  ) {}

  ngOnInit() {
    this.menus = this.authService.getMenuList() || [];
    this.tabsVisibles = this.tabs.filter((tab) => this.tieneAcceso(tab.menu));
    this.seleccionarDashboard(this.tabsVisibles[0]?.id || "");
  }

  /** Si la pestaña activa esta consultando (deshabilita "Actualizar"). */
  get cargando(): boolean {
    return this.pestana()?.cargando ?? false;
  }

  seleccionarDashboard(id: string): void {
    this.activeDashboard = id;
    const gestionar = this.tabsVisibles.find((tab) => tab.id === id)?.gestionar;
    this.gestion = gestionar && this.tieneAcceso(gestionar.menu || null) ? gestionar : null;
  }

  actualizarDashboardActivo(): void {
    this.pestana()?.actualizar();
  }

  abrirGestion(): void {
    if (this.gestion) {
      this.router.navigate([this.gestion.ruta]);
    }
  }

  /** null no gatea; con varias claves basta tener una hoja. */
  private tieneAcceso(menu: string | string[] | null): boolean {
    if (menu === null) {
      return true;
    }
    const claves = Array.isArray(menu) ? menu : [menu];
    return claves.some((clave) => tieneAccesoMenu(this.menus, clave));
  }
}
